import React, { useState, useEffect, useRef } from "react";
import { GoogleApiWrapper, Map, Marker } from "google-maps-react";
import { isMobile, isIOS } from "detect-mobile-device";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";
import usePlacesAutocomplete, { getGeocode, getLatLng } from "use-places-autocomplete";
import useOnclickOutside from "react-cool-onclickoutside";
import Loader from "../Loader/Loader";
import Modal from "../Modal/Modal";
import CONSTANTS from "../Constants";

function MapWidget(props) {
    const ref = useRef();
    const mapRef = useRef();
    const individualFieldInput = useRef();
    const [texts, setTexts] = useState({
        title: "Delivery address",
        placeholder: "Type your address",
        send: "Send location",
        cancel: "Cancel",
        noResults: "No results found",
        adjustAddress: "Adjust the location dragging the marker",
        incompleteAddress: "Complete all the fields to continue",
        exteriorNumber: "Exterior number",
        street: "Street",
        neighborhood: "Neighborhood",
        city: "City",
        state: "State",
        zipCode: "Postal code",
        cancelAction: "Cancel",
        confirmUpdate: "Update",
        locationPermissions: "Location permissions",
        gettingLocation: "Getting location...",
        requestingLocationPermission: "Requesting location permissions...",
        noPermissionGranted:
            " - We do not have permissions to obtain your address automatically, you may change the permissions in your browser settings.",
        noDetermineLocation: " - We could not determine your location, you must enter it manually.",
        locationPermissionChanged:
            " - We have detected a change in the location permissions, for get location is necessary to reload the site.",
        locationObtained:
            "- We have obtained your location, you may adjust the coordinates and data of your address.",
        confirmAddress: "Adjust direction",
        searchAddress: "Find address",
    });
    const NOT_FOUND_INDEX = -1;
    const ADDRESS_LEVELS = {
        STREET_NUMBER: "street_number",
        CITY: "locality",
        SUB_LOCALITY: "locality",
        STATE: "administrative_area_level_1",
        STREET_NAME: "route",
        POSTAL_CODE: "postal_code",
        NEIGHBORHOOD: "sublocality_level_1",
        COUNTRY: "country",
    };
    const initialState = {
        street: "",
        zipCode: "",
        neighborhood: "",
        state: "",
        exteriorNumber: "",
        country: "",
    };
    const [textComplement, setTextComplement] = useState("");
    const [labelTextUpdated, setLabelTextUpdated] = useState("");
    const [isHideModalControls, setIsHideModalControls] = useState(true);
    const [confirmationPhase, setConfirmationPhase] = useState(true);
    const [loadingPermissions, setLoadingPermissions] = useState(false);
    const [individualFieldInputValue, setIndividualFieldInputValue] = useState("");
    const [individualInputName, setIndividualInputName] = useState("");
    const [locationPermissionControl, setLocationPermissionControl] = useState("");
    const [isIncompleteAddress, setIsIncompleteAddress] = useState(false);
    const [clientAddress, setClientAddress] = useState(initialState);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSuggestions, setIsSuggestions] = useState(false);
    const [currentLocation, setCurrentLocation] = useState({
        lat: () => 0,
        lng: () => 0,
    });
    const [selectedLocation, setSelectedLocation] = useState({});
    const {
        ready,
        value,
        suggestions: { status, data },
        setValue,
        clearSuggestions,
    } = usePlacesAutocomplete({
        requestOptions: {
            location: currentLocation,
            radius: 500,
        },
        debounce: 300,
    });

    useOnclickOutside([ref, mapRef], () => {
        // When user clicks outside of the component, we can dismiss
        // the searched suggestions by calling this method
        clearSuggestions();
    });

    const handleInput = (e) => {
        // Update the keyword of the input element
        setIsSuggestions(true);
        setValue(e.target.value);
    };

    const handleSelect =
        ({ description }) =>
        () => {
            // When user selects a place, we can replace the keyword without request data from API
            // by setting the second parameter as "false"
            setValue(description, false);
            clearSuggestions();
            // Log to test all the results
            getGeocode({ address: description }).then((results) => {
                setSelectedLocation(results[0]);
                decodeAddress(results[0]);
            });
            // Get latitude and longitude via utility functions
            getGeocode({ address: description })
                .then((results) => getLatLng(results[0]))
                .then((latitudes) => {
                    // Set location on selected point
                    setCurrentLocation(new props.google.maps.LatLng(latitudes.lat, latitudes.lng));
                })
                .catch((error) => {
                    console.log("😱 Error: ", error);
                });
        };

    const validateClientAddress = function () {
        let validate = true;
        if (!clientAddress.street) {
            return;
        }
        if (!clientAddress.exteriorNumber) {
            return;
        }
        if (!clientAddress.zipCode) {
            return;
        }
        if (!clientAddress.neighborhood) {
            return;
        }
        if (!clientAddress.state) {
            return;
        }
        if (!clientAddress.city) {
            return;
        }
        return validate;
    };

    const updateField = (e) => {
        e.preventDefault();
        setClientAddress({
            ...clientAddress,
            [e.target.name]: e.target.value,
        });
    };

    const updateIndividualField = (e) => {
        e.preventDefault();
        setIndividualFieldInputValue(e.target.value);
    };

    const setFieldStatus = function (isUpdatedField) {
        if (isUpdatedField) {
            setClientAddress({
                ...clientAddress,
                [individualInputName]: individualFieldInputValue,
            });
        }
        setIsHideModalControls(false);
        setIndividualInputName("");
    };

    const startLocationPhase = function () {
        setConfirmationPhase(false);
        setIsHideModalControls(false);
    };

    const individualInput = (e) => {
        if (isMobile()) {
            setIsHideModalControls(true);
            setIndividualInputName(e.target.name);
            setIndividualFieldInputValue(e.target.value);
        }
    };

    const validateIndexOf = function (types, field) {
        return types.indexOf(field) > NOT_FOUND_INDEX;
    };

    const decodeAddress = function (results) {
        let exteriorNumber, city, street, zipCode, neighborhood, state, country;
        try {
            let i = 0;
            while (i < results.address_components.length) {
                var types = results.address_components[i].types;
                if (validateIndexOf(types, ADDRESS_LEVELS.STREET_NUMBER)) {
                    exteriorNumber = results.address_components[i].long_name.replace("#", "");
                } else if (validateIndexOf(types, ADDRESS_LEVELS.CITY)) {
                    city = results.address_components[i].long_name;
                } else if (validateIndexOf(types, ADDRESS_LEVELS.STATE)) {
                    state = results.address_components[i].long_name;
                } else if (validateIndexOf(types, ADDRESS_LEVELS.STREET_NAME)) {
                    street = results.address_components[i].long_name;
                } else if (validateIndexOf(types, ADDRESS_LEVELS.POSTAL_CODE)) {
                    zipCode = results.address_components[i].long_name;
                } else if (
                    validateIndexOf(types, ADDRESS_LEVELS.NEIGHBORHOOD) ||
                    validateIndexOf(types, ADDRESS_LEVELS.SUB_LOCALITY)
                ) {
                    neighborhood = results.address_components[i].long_name;
                } else if (validateIndexOf(types, ADDRESS_LEVELS.COUNTRY)) {
                    country = results.address_components[i].long_name;
                }
                i++;
            }
            setClientAddress({
                street: street || "",
                zipCode: zipCode || "",
                neighborhood: neighborhood || "",
                state: state || "",
                exteriorNumber: exteriorNumber || "",
                city: city || "",
                country: country || "",
            });
        } catch (err) {
            throw Error(err);
        }
    };

    const renderSuggestions = () =>
        data.map((suggestion) => {
            const {
                id,
                structured_formatting: { main_text, secondary_text },
            } = suggestion;

            return (
                <li key={id} onClick={handleSelect(suggestion)}>
                    <strong>{main_text}</strong> <small>{secondary_text}</small>
                </li>
            );
        });

    const setLocationPermission = function (state) {
        setLoadingPermissions(true);
        let labelTextUpdated = "";
        let textComplement = texts.noDetermineLocation;
        switch (state) {
            case CONSTANTS.PERMISSIONS.PERMISSION_GRANTED:
                textComplement = texts.gettingLocation;
                labelTextUpdated = "";
                break;

            case CONSTANTS.PERMISSIONS.PERMISSION_REQUESTED:
                textComplement = texts.requestingLocationPermission;
                labelTextUpdated = "";
                break;

            case CONSTANTS.PERMISSIONS.PERMISSION_DENIED:
            case CONSTANTS.GEOLOCATION_POSITION_ERRORS.PERMISSION_DENIED:
                labelTextUpdated = texts.searchAddress;
                textComplement = texts.noPermissionGranted;
                setLoadingPermissions(false);
                break;
            // The acquisition of the geolocation failed for internal error
            case CONSTANTS.GEOLOCATION_POSITION_ERRORS.POSITION_UNAVAILABLE:
                setLoadingPermissions(false);
                textComplement = texts.noDetermineLocation;
                labelTextUpdated = texts.searchAddress;
                break;
            // The time allowed to acquire the geolocation, defined by PositionOptions.timeout information was reached
            case CONSTANTS.GEOLOCATION_POSITION_ERRORS.TIMEOUT:
                setLoadingPermissions(false);
                textComplement = texts.noDetermineLocation;
                labelTextUpdated = texts.searchAddress;
                break;

            case CONSTANTS.PERMISSIONS.PERMISSION_CHANGED:
                setLoadingPermissions(false);
                textComplement = texts.locationPermissionChanged;
                labelTextUpdated = texts.searchAddress;
                break;

            case CONSTANTS.PERMISSIONS.LOCATION_GETTED:
                setLoadingPermissions(false);
                textComplement = texts.locationObtained;
                labelTextUpdated = texts.confirmAddress;
                break;

            default:
                setLoadingPermissions(false);
                labelTextUpdated = texts.searchAddress;
                break;
        }
        setTextComplement(textComplement);
        setLabelTextUpdated(labelTextUpdated);
    };

    const sendLocation = () => {
        if (!validateClientAddress()) {
            setIsIncompleteAddress(true);
            return;
        }
        try {
            let i = 0;
            while (i < selectedLocation.address_components.length) {
                var types = selectedLocation.address_components[i].types;
                if (validateIndexOf(types, ADDRESS_LEVELS.STREET_NUMBER)) {
                    selectedLocation.address_components[i].long_name = clientAddress.exteriorNumber;
                } else if (validateIndexOf(types, ADDRESS_LEVELS.CITY)) {
                    selectedLocation.address_components[i].long_name = clientAddress.city;
                } else if (validateIndexOf(types, ADDRESS_LEVELS.STATE)) {
                    selectedLocation.address_components[i].long_name = clientAddress.state;
                } else if (validateIndexOf(types, ADDRESS_LEVELS.STREET_NAME)) {
                    selectedLocation.address_components[i].long_name = clientAddress.street;
                } else if (validateIndexOf(types, ADDRESS_LEVELS.POSTAL_CODE)) {
                    selectedLocation.address_components[i].long_name = clientAddress.zipCode;
                } else if (
                    validateIndexOf(types, ADDRESS_LEVELS.NEIGHBORHOOD) ||
                    validateIndexOf(types, ADDRESS_LEVELS.SUB_LOCALITY)
                ) {
                    selectedLocation.address_components[i].long_name = clientAddress.neighborhood;
                } else if (validateIndexOf(types, ADDRESS_LEVELS.COUNTRY)) {
                    selectedLocation.address_components[i].long_name = clientAddress.country;
                }
                i++;
            }
            selectedLocation.formatted_address = `${clientAddress.street} ${clientAddress.exteriorNumber}, ${clientAddress.neighborhood}, ${clientAddress.zipCode}, ${clientAddress.city} ${clientAddress.state}, ${clientAddress.country}`;
            setIsIncompleteAddress(false);
            clearSuggestions();
            setValue("");
            props.selectLocationCb(CONSTANTS.CONTENT_TYPE.MAP, selectedLocation);
            setIsModalOpen(false);
        } catch (error) {
            throw Error(error);
        }
    };

    const closeModal = function () {
        clearSuggestions();
        setValue("");
        setIsModalOpen(false);
    };

    const noResultsFound = function () {
        setSelectedLocation();
        setValue("");
    };

    const onPositionChanged = function (coord) {
        const lat = coord.lat();
        const lng = coord.lng();
        // eslint-disable-next-line
        const geocoder = new props.google.maps.Geocoder();
        geocoder.geocode({ location: { lat: lat, lng: lng } }, function (results, status) {
            if (status === "OK") {
                if (results[0]) {
                    setIsSuggestions(false);
                    setSelectedLocation(results[0]);
                    setValue(results[0].formatted_address);
                    decodeAddress(results[0]);
                } else {
                    noResultsFound();
                    setClientAddress(initialState);
                }
            } else {
                noResultsFound();
                setClientAddress(initialState);
                console.log("Geocoder failed due to: " + status);
            }
        });
        setCurrentLocation(new props.google.maps.LatLng(lat, lng));
    };

    const showPosition = function (revealPosition) {
        if (revealPosition) {
            setLocationPermission(CONSTANTS.PERMISSIONS.LOCATION_GETTED);
            const latLng = new props.google.maps.LatLng(
                revealPosition.coords.latitude,
                revealPosition.coords.longitude
            );
            onPositionChanged(latLng);
            setCurrentLocation(latLng);
        }
    };

    const onErrorShowPosition = function (error) {
        setDefaultPosition();
        let errorCode = CONSTANTS.GEOLOCATION_POSITION_ERRORS.GENERAL_ERROR;
        if (error && error.code) {
            errorCode = error.code;
        }
        setLocationPermission(errorCode);
    };

    const setDefaultPosition = function () {
        let defaultLocation = {
            lat: 0,
            lng: 0,
        };
        if (props.defaultLocation && props.defaultLocation.lat && props.defaultLocation.lng) {
            defaultLocation = {
                lat: props.defaultLocation.lat,
                lng: props.defaultLocation.lng,
            };
        }
        setCurrentLocation(new props.google.maps.LatLng(defaultLocation.lat, defaultLocation.lng));
    };

    const getCurrentPosition = function () {
        navigator.geolocation.getCurrentPosition(showPosition, onErrorShowPosition, {
            timeout: 30000,
        });
    };

    const reportLocationPermissionChange = function (state) {
        if (state) {
            let stateToReport = state;
            getCurrentPosition();
            if (locationPermissionControl === CONSTANTS.PERMISSIONS.PERMISSION_DENIED) {
                stateToReport = CONSTANTS.PERMISSIONS.PERMISSION_CHANGED;
            }
            setLocationPermission(stateToReport);
        }
    };

    const undeterminedLocationError = function () {
        setDefaultPosition();
        setLocationPermissionControl(CONSTANTS.GEOLOCATION_POSITION_ERRORS.POSITION_UNAVAILABLE);
        setLocationPermission(CONSTANTS.GEOLOCATION_POSITION_ERRORS.POSITION_UNAVAILABLE);
    };

    const openModalInstance = function () {
        setIsModalOpen(!isModalOpen);
        // obtain the location permission state from browser
        try {
            if (isIOS()) {
                setLocationPermissionControl(CONSTANTS.PERMISSIONS.PERMISSION_REQUESTED);
                setLocationPermission(CONSTANTS.PERMISSIONS.PERMISSION_REQUESTED);
                getCurrentPosition();
            } else {
                window.navigator.permissions
                    .query({
                        name: "geolocation",
                    })
                    .then(function (result) {
                        if (result && result.state) {
                            getCurrentPosition(result.state);
                            setLocationPermissionControl(result.state);
                            setLocationPermission(result.state);
                        }
                        result.onchange = function () {
                            if (result && result.state) {
                                reportLocationPermissionChange(result.state);
                            }
                        };
                    })
                    .catch(function () {
                        undeterminedLocationError();
                    });
            }
        } catch (error) {
            undeterminedLocationError();
        }
    };

    useEffect(() => {
        if (props.language === "SPANISH") {
            setTexts({
                title: "Dirección de entrega",
                placeholder: "Ingrese su dirección",
                send: "Enviar dirección",
                cancel: "Cancelar",
                noResults: "No se encontrarón resultados",
                adjustAddress: "Ajusta la dirección arrastrando el marcador",
                incompleteAddress: "Completa todos los campos para continuar",
                exteriorNumber: "Número exterior",
                street: "Calle",
                neighborhood: "Colonia",
                city: "Ciudad",
                state: "Estado",
                zipCode: "Código postal",
                cancelAction: "Cancelar",
                confirmUpdate: "Actualizar",
                locationPermissions: "Permisos de ubicación",
                gettingLocation: "Obteniendo ubicación...",
                requestingLocationPermission: "Solicitando permisos de ubicación...",
                noPermissionGranted:
                    " - No tenemos permisos para obtener tu dirección automáticamente, puedes cambiar los permisos en los ajustes de tu navegador.",
                noDetermineLocation:
                    " - No pudimos determinar tu dirección, deberás ingresarla manualmente.",
                locationPermissionChanged:
                    " - Hemos detectado un cambio en los permisos de ubicación, necesario recargar el sitio.",
                locationObtained:
                    "- Hemos obtenido tu dirección, puedes ajustar las coordenadas y datos de tu dirección.",
                confirmAddress: "Ajustar dirección",
                searchAddress: "Búscar dirección",
            });
        }
        // eslint-disable-next-line
    }, []);

    return (
        <div className="opt-map-widget">
            <Modal
                show={isModalOpen}
                acceptCb={sendLocation}
                hideControlsCb={isHideModalControls}
                closeCb={closeModal}
                acceptText={texts.send}
                cancelText={texts.cancel}
                disabled={!value}
            >
                {confirmationPhase ? (
                    <div className="opt-map-widget__modal-content">
                        <div className="opt-row">
                            <div className="opt-col">
                                <h2>{texts.locationPermissions}</h2>
                                <p>{textComplement}</p>
                                <Loader isLoading={loadingPermissions} />
                                {labelTextUpdated ? (
                                    <button
                                        className="opt-map-widget__button-small"
                                        onClick={startLocationPhase}
                                    >
                                        {labelTextUpdated}
                                    </button>
                                ) : null}
                            </div>
                        </div>
                    </div>
                ) : null}
                {!confirmationPhase && individualInputName ? (
                    <div className="opt-map-widget__modal-content">
                        <div className="opt-row">
                            <div className="opt-col">
                                <label className="opt-map-widget__confirm-cancel-edit-individual-field-label opt-map-widget__map-label-input-individual">
                                    {texts[individualInputName]}
                                </label>
                                <input
                                    ref={individualFieldInput}
                                    id="input-individual-field"
                                    className="opt-map-widget__map-input opt-input opt-input--border-bottom"
                                    name="individualFieldInput"
                                    onChange={updateIndividualField}
                                    value={individualFieldInputValue || ""}
                                    placeholder={texts[individualInputName]}
                                />
                            </div>
                        </div>
                        <div className="opt-row">
                            <div className="opt-col">
                                <button
                                    className="opt-map-widget__button-small-light"
                                    onClick={function () {
                                        setFieldStatus(false);
                                    }}
                                >
                                    <FontAwesomeIcon
                                        icon={faTimes}
                                        className="opt-map-widget__confirm-cancel-edit-individual-field-icon-light"
                                    />
                                    {texts.cancelAction}
                                </button>
                            </div>
                        </div>
                        <div className="opt-row">
                            <div className="opt-col">
                                <button
                                    className="opt-map-widget__button-small"
                                    onClick={function () {
                                        setFieldStatus(true);
                                    }}
                                >
                                    <FontAwesomeIcon
                                        icon={faCheck}
                                        className="opt-map-widget__confirm-cancel-edit-individual-field-icon"
                                    />
                                    {texts.confirmUpdate}
                                </button>
                            </div>
                        </div>
                    </div>
                ) : null}
                {!confirmationPhase && !individualInputName ? (
                    <div className="opt-map-widget__modal-content">
                        <div className="opt-row">
                            <div className="opt-col">
                                <h4>{texts.title}</h4>
                                <div className="opt-map-widget__search-box" ref={ref}>
                                    <label className="opt-map-widget__confirm-cancel-edit-individual-field-label">
                                        {texts.searchAddress}
                                    </label>
                                    <input
                                        className="opt-input"
                                        value={value}
                                        onChange={handleInput}
                                        disabled={!ready}
                                        placeholder={texts.placeholder}
                                    />
                                    {status === "OK" && (
                                        <ul className="opt-map-widget__list">
                                            {isSuggestions ? renderSuggestions() : null}
                                        </ul>
                                    )}
                                </div>
                                <div
                                    className="opt-map-widget__map"
                                    ref={mapRef}
                                    style={{ width: "100%" }}
                                >
                                    <Map
                                        initialCenter={{
                                            lat: currentLocation.lat(),
                                            lng: currentLocation.lng(),
                                        }}
                                        center={{
                                            lat: currentLocation.lat(),
                                            lng: currentLocation.lng(),
                                        }}
                                        gestureHandling={"greedy"}
                                        fullscreenControl={false}
                                        mapTypeControl={false}
                                        streetViewControl={false}
                                        google={props.google}
                                        zoom={14}
                                    >
                                        <Marker
                                            draggable={true}
                                            onDragend={(t, map, coord) =>
                                                onPositionChanged(coord.latLng)
                                            }
                                            position={{
                                                lat: currentLocation.lat(),
                                                lng: currentLocation.lng(),
                                            }}
                                        />
                                    </Map>
                                </div>
                            </div>
                        </div>
                        <div className="opt-map-widget__map_fields">
                            <div className="opt-row">
                                <div className="opt-col opt-col-5">
                                    <label className="opt-map-widget__map-label">
                                        {texts.street}
                                    </label>
                                    <input
                                        className="opt-map-widget__map-input opt-input opt-input--border-bottom"
                                        id="input-street"
                                        name="street"
                                        onFocus={individualInput}
                                        onChange={updateField}
                                        value={clientAddress.street || ""}
                                        placeholder={texts.street}
                                    />
                                </div>
                                <div className="opt-col opt-col-5">
                                    <label className="opt-map-widget__map-label">
                                        {texts.exteriorNumber}
                                    </label>
                                    <input
                                        className="opt-map-widget__map-input opt-input opt-input--border-bottom"
                                        id="input-exteriorNumber"
                                        name="exteriorNumber"
                                        onFocus={individualInput}
                                        onChange={updateField}
                                        value={clientAddress.exteriorNumber || ""}
                                        placeholder={texts.exteriorNumber}
                                    />
                                </div>
                            </div>
                            <div className="opt-row">
                                <div className="opt-col opt-col-5">
                                    <label className="opt-map-widget__map-label">
                                        {texts.zipCode}
                                    </label>
                                    <input
                                        className="opt-map-widget__map-input opt-input opt-input--border-bottom"
                                        id="input-zipCode"
                                        name="zipCode"
                                        onFocus={individualInput}
                                        onChange={updateField}
                                        value={clientAddress.zipCode || ""}
                                        placeholder={texts.zipCode}
                                    />
                                </div>
                                <div className="opt-col opt-col-5">
                                    <label className="opt-map-widget__map-label">
                                        {texts.neighborhood}
                                    </label>
                                    <input
                                        className="opt-map-widget__map-input opt-input opt-input--border-bottom"
                                        id="input-neighborhood"
                                        name="neighborhood"
                                        onFocus={individualInput}
                                        onChange={updateField}
                                        value={clientAddress.neighborhood || ""}
                                        placeholder={texts.neighborhood}
                                    />
                                </div>
                            </div>
                            <div className="opt-row">
                                <div className="opt-col opt-col-5">
                                    <label className="opt-map-widget__map-label">
                                        {texts.state}
                                    </label>
                                    <input
                                        className="opt-map-widget__map-input opt-input opt-input--border-bottom"
                                        id="input-state"
                                        name="state"
                                        onFocus={individualInput}
                                        onChange={updateField}
                                        value={clientAddress.state || ""}
                                        placeholder={texts.state}
                                    />
                                </div>
                                <div className="opt-col opt-col-5">
                                    <label className="opt-map-widget__map-label">
                                        {texts.city}
                                    </label>
                                    <input
                                        className="opt-map-widget__map-input opt-input opt-input--border-bottom"
                                        id="input-city"
                                        name="city"
                                        onFocus={individualInput}
                                        onChange={updateField}
                                        value={clientAddress.city || ""}
                                        placeholder={texts.city}
                                    />
                                </div>
                            </div>
                        </div>
                        {isIncompleteAddress ? (
                            <div className="opt-map-widget__incomplete-address">
                                {texts.incompleteAddress}
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </Modal>
            <span className="opt-map-widget__text" onClick={() => openModalInstance()}>
                {" "}
                {props.text}
            </span>
        </div>
    );
}
export default GoogleApiWrapper((props) => {
    return {
        apiKey: props.mapKey,
    };
})(MapWidget);
